import {
	AfterViewInit,
	Component,
	QueryList,
	ViewChild,
	ViewChildren,
	ViewEncapsulation
} from '@angular/core';
import { DynamicHostComponent } from '../dynamic-host/dynamic-host.component';
import { DynamicHostModel } from '../model/dynamic-host.model';
import { EventService } from '../service/event.service';
import { AbstractModal } from '../model/abstract.modal';
import { ModalEventType } from '../model/modal.model';

@Component({
	selector: 'cv-modal',
	styleUrls: ['./modal.component.scss'],
	encapsulation: ViewEncapsulation.None,
	templateUrl: './modal.component.html',
})
export class ModalComponent implements AfterViewInit {
	@ViewChildren(DynamicHostComponent) dynamicHostList!: QueryList<DynamicHostComponent>;
	@ViewChild('modal', { static: true }) modalElement: any;
	// Contains a list of every available dialog open as an Stack
	dynamicModalList: DynamicHostModel[] = <any>[];
	private newDialog = false;

	constructor(private eventService: EventService) {
		this.registerDialog();
	}

	ngAfterViewInit(): void {
		// Wait to be registered
		this.dynamicHostList.changes.subscribe((dynamicHostComponent: any) => {
			setTimeout(() => {
				// We get the lasted change added
				if (
					this.newDialog &&
					dynamicHostComponent &&
					!dynamicHostComponent.last.currentDialogComponentInstance
				) {
					this.newDialog = false;
					if (this.dynamicModalList) {
						const dynamicHostModel = this.dynamicModalList[this.dynamicModalList.length - 1];
						// Save to the List which host component is attached
						dynamicHostModel.dynamicHostComponent = this.dynamicHostList.last;
						this.createComponent(dynamicHostModel);
					}
				}
			});
		});
	}

	/**
	 * Register the event, so it can be listen to on open events or clear current instance
	 */
	public registerDialog(): void {
		this.eventService.on(ModalEventType.OPEN, (event: any) => {
			this.newDialog = true;
			// We initialize the Model with the incoming Model event
			const dynamicHostModel: DynamicHostModel = {
				modalModel: event.event,
				instantiated: false,
			};

			// We add a new Empty element to the dynamicDialogList
			this.dynamicModalList.push(dynamicHostModel);
		});
	}

	/**
	 * Create the component and clear previous data on it
	 * @param componentFactoryResolver
	 */
	public createComponent(dynamicHostModel: DynamicHostModel): void {
		try {
			// Create new instance
			const componentFactory = dynamicHostModel.modalModel.componentFactoryResolver.resolveComponentFactory(
				dynamicHostModel.modalModel.component
			);
			const currentViewContainerRef = dynamicHostModel.dynamicHostComponent.dynamicContent.viewContainerRef;
			currentViewContainerRef.clear();
			const componentRef = currentViewContainerRef.createComponent(componentFactory);

			const currentDialogComponentInstance = <AbstractModal>componentRef.instance;
			currentDialogComponentInstance.data = dynamicHostModel.modalModel.data;
			currentDialogComponentInstance.content = dynamicHostModel.modalModel.content;
			currentDialogComponentInstance.buttons = [];

			// Overwrite the property configuration
			if (dynamicHostModel.modalModel.modalConfiguration) {
				dynamicHostModel.dynamicHostComponent.modalConfigurationModel = Object.assign(
					dynamicHostModel.dynamicHostComponent.modalConfigurationModel,
					dynamicHostModel.modalModel.modalConfiguration
				);
				// If the user wants to set the background as empty, we change the default so we can revert it to that
				if (!dynamicHostModel.dynamicHostComponent.modalConfigurationModel.showBackground) {
					dynamicHostModel.dynamicHostComponent.modalConfigurationModel.setDefaultShowBackground(
						dynamicHostModel.dynamicHostComponent.modalConfigurationModel.showBackground
					);
				}
			}

			// Save the instance
			dynamicHostModel.dynamicHostComponent.currentModalComponentInstance = currentDialogComponentInstance;

			// Before to open the Dialog, we hide any other background
			// this.showHideBackgrounds();

			// Emits on Success
			if (currentDialogComponentInstance.successEvent) {

				currentDialogComponentInstance.successEvent.subscribe(result => {

					dynamicHostModel.modalModel.observable.next(result);
					dynamicHostModel.modalModel.observable.complete();
					// Last element of the array only
					this.dynamicModalList.pop();
					setTimeout(() => {
						// After close a Dialog, we show any other background
						// this.showHideBackgrounds();
					});

				});
			}
			setTimeout(() => {
				dynamicHostModel.dynamicHostComponent.publishDialog();
				dynamicHostModel.instantiated = true;
				this.onNewDialogInstantiated();
			});

		} catch (e) {
			console.error("Dialog can't be instantiated/created", e);
		}
	}

	/**
	 * This method gets the position in the canvas of a given element
	 * @param el
	 */

	private getOffset(el: any): any {
		let _x = 0;
		let _y = 0;
		while (el && !isNaN(el.offsetLeft) && !isNaN(el.offsetTop)) {
			_x += el.offsetLeft - el.scrollLeft;
			_y += el.offsetTop - el.scrollTop;
			el = el.offsetParent;
		}
		return { top: _y, left: _x };
	}

	/**
	 * This method will help to setup the focus to the first action button
	 * **/
	private setFocusOnActionButton(dynamicHostComponent: DynamicHostComponent): void {
		if (dynamicHostComponent && dynamicHostComponent.actionButtons && dynamicHostComponent.actionButtons.length > 0) {
			dynamicHostComponent.actionButtons['first'].nativeElement.focus();
		}
	}

	/**
	 * After a new Dialog open, we hide the background backtrack
	 */
	private showHideBackgrounds(): void {
		this.dynamicHostList.forEach((dynamicHostComponent: DynamicHostComponent, index) => {
			dynamicHostComponent.modalConfigurationModel.showBackground = dynamicHostComponent.modalConfigurationModel.getDefaultShowBackground();

			const firstDynamicHostComponent: DynamicHostComponent = this.dynamicHostList.first;

			if (firstDynamicHostComponent === dynamicHostComponent) {
				dynamicHostComponent.modalConfigurationModel.showBackground = true;
			} else {
				dynamicHostComponent.modalConfigurationModel.showBackground = false;
			}
		});
	}

	/**
	 * On New Dialog instantiated.
	 * @private
	 */
	private onNewDialogInstantiated(): void {
		if (this.dynamicModalList.length > 0) {
			const modalComponent = this.dynamicModalList[this.dynamicModalList.length - 1];
			setTimeout(() => this.setFocusOnActionButton(modalComponent.dynamicHostComponent), 100);
		}
	}
}
